/**
 * A default style to use when presenting react-hot-toast.
 */
export const toastTheme = {
  style: {
    border: '3px solid #FAA21B',
    background: '#FAA21B',
    padding: '16px',
    color: '#132048',
  },
  iconTheme: {
    primary: '#132048',
    secondary: '#FFFAEE',
  },
}

/**
 * A generic unit that represents the type of responsive
 * values available in tailwind.
 */
type ResponsiveValue = string | number | null | undefined

/**
 * The available responsive prefixes we can use in tailwind.
 */
const SIZES = ['', 'sm:', 'md:', 'lg:', 'xl:']

export type TailwindPrefix =
  | 'p-'
  | 'px-'
  | 'py-'
  | 'pl-'
  | 'pr-'
  | 'pb-'
  | 'pt-'
  | 'm-'
  | 'mx-'
  | 'my-'
  | 'ml-'
  | 'mr-'
  | 'mb-'
  | 'mt-'
  | 'h-'
  | 'w-'
  | 'min-h-'
  | 'max-h-'
  | 'min-w-'
  | 'max-w-'

/**
 * Generates a tailwind style or set of styles. This miimicks
 * the behavior of rebass but generates tailwind styles to yield
 * the same effect.
 * @param val The value or values for a responsive style.
 * @param prefix The tailwind prefix such as 'p-' or 'w-'
 */
export const makeResponsive = (
  val: ResponsiveValue | ResponsiveValue[],
  prefix: TailwindPrefix
) =>
  typeof val === 'object'
    ? val
        ?.map((v, i) => (v ? [SIZES[i], prefix, v].join('') : ''))
        .filter((s) => s.length > 0)
        .join(' ') ?? ''
    : val
    ? [prefix, val].join('')
    : ''

/**
 * An enumeration of possible text alignment values for tailwind.
 */
export type TextAlignment = 'left' | 'right' | 'center' | 'justify'

/**
 * An enumeration of possible padding / margin values for tailwind.
 */
export type Spacing =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 8
  | 10
  | 12
  | 16
  | 20
  | 24
  | 40
  | 48
  | 56
  | 64
  | 'px'
  | null

/**
 * An enumeration of possible height values for tailwind.
 */
export type Height =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 8
  | 10
  | 12
  | 16
  | 20
  | 24
  | 40
  | 48
  | 56
  | 64
  | 'auto'
  | 'px'
  | 'full'
  | 'screen'
  | null

/**
 * An enumeration of possible width values for tailwind.
 */
export type Width =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 8
  | 10
  | 12
  | 16
  | 20
  | 24
  | 32
  | 40
  | 48
  | 56
  | 64
  | 'px'
  | 'screen'
  | 'full'
  | 'auto'
  | '1/2'
  | '1/3'
  | '1/4'
  | '1/5'
  | '1/6'
  | '1/12'
  | '2/3'
  | '2/4'
  | '2/5'
  | '2/6'
  | '2/12'
  | '3/4'
  | '3/5'
  | '3/6'
  | '3/12'
  | '4/5'
  | '4/6'
  | '4/12'
  | '5/6'
  | '5/12'
  | '6/12'
  | '7/12'
  | '8/12'
  | '9/12'
  | '10/12'
  | '11/12'
  | null

/**
 * Theme configuration for StyledComponents. To be deprecated once we migrate off of SC to tailwind.
 */
export interface ITheme {
  borderRadius: string
  breakpoints: string[]
  colorAccent: string
  colorActionable: string
  colorClear: string
  colorcontentareabackground: string
  colorcontentareabackgroundalt: string
  colorContentAreaBorder: string
  colorcontentareaborderselected: string
  colorHeading: string
  colorHeadingSub: string
  colorAlertBackground: string
  colorBody: string
  colorInactive: string
  colorBodyInverted: string
  colorDestructive: string
  colorError: string
  colorFavorite: string
  colorPrimary: string
  colorSecondary: string
  colornavbackground: string
  colornavactive: string
  colornavbackgroundactive: string
  colornavbackgroundactivealt: string
  colorNavContent: string
  colorNavContentAlt: string
  colorNavDivider: string
  colornavdivideralt: string
  colorNavSectionBackground: string
  colorNavSectionBackgroundAlt: string
  colormetricbackground: string
  fontFamilyBody: string
  fontFamilyHeader: string
  fonts: {
    body: string
    heading: string
    monospace: string
  }
  fontWeights: {
    body: number
    heading: number
    bold: number
  }
}

export const theme: ITheme = {
  borderRadius: '0.25rem',
  breakpoints: ['48em', '64em', '75em'],
  colorAccent: '#FAA21B',
  colorActionable: '#F26724',
  colorAlertBackground: '#FAA21B',
  colorBody: '#000000',
  colorBodyInverted: '#fff',
  colorClear: 'rgba(0,0,0,0)',
  colorcontentareabackground: '#fff',
  colorcontentareabackgroundalt: '#F3F3F3',
  colorContentAreaBorder: '#dcddde',
  colorcontentareaborderselected: '#1E988A',
  colorDestructive: '#CE342A',
  colorError: '#CE342A',
  colorFavorite: '#f90',
  colorHeading: '#132048',
  colorHeadingSub: '#132048',
  colorInactive: '#9b9b9b',
  colormetricbackground: '#6e54a0',
  colornavbackground: '#132048',
  colornavbackgroundactive: '#1a2d5a',
  colornavbackgroundactivealt: '#243a6b',
  colornavactive: '#1a2d5a',
  colorNavContent: '#9ebcdb',
  colorNavContentAlt: '#bed5ea',
  colorNavDivider: '#0e1a3d',
  colornavdivideralt: '#243a6b',
  colorNavSectionBackground: '#0a1230',
  colorNavSectionBackgroundAlt: '#111d45',
  colorPrimary: '#006EB5',
  colorSecondary: '#132048',
  fontFamilyBody:
    '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontFamilyHeader:
    '"Poppins", "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontWeights: {
    body: 400,
    bold: 700,
    heading: 700,
  },
  fonts: {
    body: 'system-ui, sans-serif',
    heading: 'inherit',
    monospace: 'Menlo, monospace',
  },
}
